import { useMemo } from 'react';

import { isEqual, isNil } from 'lodash-es';

import { PricingBodInfo } from '../types';
import { usePricingAndLeadTimeBodList } from '../../pricing&LeadTime/queries';
import { useGetBomDataQuery, useGetDatasheetTabDataQuery, useGetOmDataQuery } from '../../queries';
import { BodResponse, DatasheetTabDataResponse, OmDataResponse } from '../../queries/apiTypes';
import { SPECIFICATION_VALUES } from '../constants';

const DEFAULT_VALUES_FOR_BODS: Partial<BodResponse> = {
    category: '',
    description: '',
    manufacturer: '',
    model_number: '',
    qty: null,
    specification: SPECIFICATION_VALUES.BASIS_OF_DESIGN,
    product_to_replace: null,
    is_kit: false
} as const;

const bodFieldsForBoolConversion = new Set<string>(['category', 'description', 'manufacturer', 'model_number', 'specification', 'is_kit']);

type PricingBodWithoutId = Omit<PricingBodInfo, 'bod_id'>;

const DEFAULT_VALUES_FOR_QUOTES: PricingBodWithoutId = {
    notes: '',
    lot_id: null,
    cost_cents: null,
    discount_percentage: null,
    discounted_cost_cents: null,
    extended_discounted_cost_cents: null,
    margin_percentage: null,
    sell_price_cents: null,
    lead_time_unit: null,
    lead_time_min: null,
    lead_time_max: null,
    source_quote_number: null,
    internal_notes: null,
    per: 1
} as const;

type PartialOmDataResponse = Omit<OmDataResponse, 'bod_id'>;
const DEFAULT_VALUES_FOR_OM: PartialOmDataResponse = {
    selected_drawing: {},
    selected_installation_guide: {},
    selected_other_document: {},
    selected_warranty: {}
} as const;

type PartialDatasheetResponse = Omit<DatasheetTabDataResponse, 'bod_id' | 'all_pages_hidden'>;
const DEFAULT_VALUES_FOR_DATASHEET: PartialDatasheetResponse = {
    notes: '',
    selected_datasheet: {},
    selected_datasheet_id: null
} as const;

const stringPricingFields = new Set<string>(['notes', 'lead_time_unit', 'source_quote_number', 'internal_notes']);

const checkIsBodEmpty = (bodData: BodResponse, pricingBodData: PricingBodInfo, omData: OmDataResponse, datasheetData: DatasheetTabDataResponse) => {
    // check if bod's basic fields are different from default values
    const isUnchangedBodData = Object.entries(DEFAULT_VALUES_FOR_BODS).every(([key, value]) => {
        const bodValue = bodData[key as keyof Partial<BodResponse>];
        if (bodFieldsForBoolConversion.has(key)) {
            return Boolean(bodValue) === Boolean(value);
        }

        return bodValue === value;
    });

    const inUnchangedPricingData = Object.entries(DEFAULT_VALUES_FOR_QUOTES).every(([key, value]) => {
        const pricingBodValue = pricingBodData[key as keyof PricingBodWithoutId];

        if (stringPricingFields.has(key)) {
            // To resolve the issues of empty string comparison with null or undefined
            return Boolean(pricingBodValue) === Boolean(value);
        }
        return pricingBodValue === value;
    });

    const isUnchangedOmData = Object.entries(DEFAULT_VALUES_FOR_OM).every(([key, value]) => {
        const omDataValue = omData[key as keyof PartialOmDataResponse];
        return isEqual(omDataValue, value);
    });

    const isUnchangedDatasheetData = Object.entries(DEFAULT_VALUES_FOR_DATASHEET).every(([key, value]) => {
        const fieldKey = key as keyof PartialDatasheetResponse;
        const datasheetValue = datasheetData[fieldKey];

        switch (fieldKey) {
            case 'notes':
                return Boolean(datasheetValue) === Boolean(value);
            case 'selected_datasheet_id':
                return datasheetValue === value;
            case 'selected_datasheet':
                return isEqual(datasheetValue, value);
            default:
                // will never fall here though
                return isEqual(datasheetValue, value);
        }
    });

    const isEmpty = isUnchangedBodData && inUnchangedPricingData && isUnchangedOmData && isUnchangedDatasheetData;
    return isEmpty;
};

export default function useGetEmptyBods(
    bomId: string,
    sectionId: number | null | undefined,
    isDetailLoading: boolean = false,
    sectionType: 'section' | 'substitute_section' = 'section',
    // all: Return all empty bods
    // single: Return if there's only single bod and that's also empty, else []
    checkScope: 'all' | 'single' = 'single'
) {
    const { data: bomData, isLoading: isBomDataLoading } = useGetBomDataQuery(bomId, { enabled: !!bomId });
    const { data: pricingBodsList, isLoading: isPricingBodListLoading } = usePricingAndLeadTimeBodList(Number(bomId), {
        enabled: !!bomId
    });

    const { data: omData, isLoading: isOmDataLoading } = useGetOmDataQuery(bomId, {
        enabled: !!bomId
    });

    const { data: datasheetTabData, isLoading: isDatasheetDataLoading } = useGetDatasheetTabDataQuery(bomId, {
        enabled: !!bomId
    });

    const emptyBods = useMemo(() => {
        if (isNil(sectionId) || isDetailLoading || !pricingBodsList?.data || !bomData?.data || !omData?.data || !datasheetTabData?.data) return [];

        const bodsInSection = bomData.data.filter((bod) => bod[sectionType] === sectionId);

        // No bodId present in the seelcted section
        if (bodsInSection.length === 0) return [];

        // multiple bodIds present, but check scope is single so return null
        if (checkScope === 'single' && bodsInSection.length > 1) return [];

        const pricingBodObj = pricingBodsList.data.reduce<{ [bodId: string]: PricingBodInfo }>((result, curr) => {
            result[curr.bod_id] = curr;
            return result;
        }, {});

        const omDataObj = omData.data.reduce<{ [bodId: string]: OmDataResponse }>((result, curr) => {
            result[curr.bod_id] = curr;
            return result;
        }, {});

        const datasheetDataObj = datasheetTabData.data.reduce<{ [bodId: string]: DatasheetTabDataResponse }>((result, curr) => {
            result[curr.bod_id] = curr;
            return result;
        }, {});

        const emptyBods = bodsInSection.filter((bodItem) => {
            const pricingDataItem = pricingBodObj[bodItem.id];
            const omDataItem = omDataObj[bodItem.id];
            const datasheetDataItem = datasheetDataObj[bodItem.id];

            // Ideally pricingDataItem, omDataItem and datasheetDataItem should always be there,
            // but if for some reason, didn't get that, do not add that in empty array.
            if (!pricingDataItem || !omDataItem || !datasheetDataItem) return false;
            return checkIsBodEmpty(bodItem, pricingDataItem, omDataItem, datasheetDataItem);
        });

        return emptyBods;
    }, [isDetailLoading, pricingBodsList?.data, bomData?.data, datasheetTabData?.data, omData?.data, sectionId, sectionType, checkScope]);

    const isLoading = isDetailLoading || isBomDataLoading || isPricingBodListLoading || isOmDataLoading || isDatasheetDataLoading;

    return {
        isLoading,
        emptyBods
    };
}
